import { FTextField } from '@components/form/FTextField';
import { type FC, useMemo, useState } from 'react';
import { type ManageTagSchemaType, manageTagControlNames } from './schema';
import { FSelectField } from '@components/form/FSelectField';
import { t } from 'i18next';
import { useListCustomPropertyOptions } from 'api/customProperty/customPropertyService';
import { type UseFormReturn } from 'react-hook-form';
import { type CustomPropertyOption } from 'interfaces/customProperty/customProperty.interface';
import { FMultiComboBox } from '@components/form/FMultiComboBox';
import { type DropdownOptions } from '@components/inputs/SelectInput/MultiSelectInput/MultiSelectInput';
import { useDebounce } from 'hooks/useDebounce';
import { useListAssets } from 'api/assets/assetsServices';

interface ManageTagFormProps {
  methods: UseFormReturn<ManageTagSchemaType>;
  initialValues?: ManageTagSchemaType;
}

const ManageTagForm: FC<ManageTagFormProps> = ({ methods, initialValues }) => {
  const [query, setQuery] = useState('');
  const { data: customPropertyOptions } = useListCustomPropertyOptions();
  const debouncedQuery = useDebounce(query, 300);

  const {
    data: buildingList,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useListAssets({
    query: debouncedQuery,
  });

  const buildingOptions = useMemo(() => {
    if (!buildingList) return [];

    return buildingList.pages.flatMap((page) =>
      page.assets.map((asset) => ({
        value: asset.id,
        name: asset.name,
      }))
    );
  }, [buildingList]);

  const buildingOptionsList = useMemo(() => {
    if (!buildingOptions) return [];

    return buildingOptions.map((building) => ({
      value: building.value,
      name: building.name,
    }));
  }, [buildingOptions]);

  const assetsInfiniteQueryKeys = useMemo(
    () => ['listAssets', debouncedQuery, '', ''],
    [debouncedQuery]
  );

  const customPropertyOptionsList = useMemo(() => {
    if (!customPropertyOptions) return [];

    return customPropertyOptions.map(
      (customPropertyOption: CustomPropertyOption) => ({
        value: customPropertyOption.key,
        name: customPropertyOption.value,
      })
    );
  }, [customPropertyOptions]);

  return (
    <form {...methods}>
      <div className="flex flex-col gap-6">
        <FSelectField
          options={customPropertyOptionsList}
          label={t('assetsPage.TagsTab.form.labels.name')}
          name={manageTagControlNames.name}
          placeholder={t('assetsPage.TagsTab.form.placeholders.name')}
          data-testid="customPropertyOption"
        />
        <FTextField
          label={t('assetsPage.TagsTab.form.labels.description')}
          name={manageTagControlNames.description}
          placeholder={t('assetsPage.TagsTab.form.placeholders.description')}
          data-testid="description"
        />
        <FMultiComboBox
          label={t('assetsPage.TagsTab.form.labels.addBuildings')}
          name={manageTagControlNames.buildings}
          query={query}
          handleQuery={setQuery}
          options={buildingOptionsList}
          isFetching={isFetching}
          placeholder={t('assetsPage.TagsTab.form.placeholders.addBuildings')}
          data-testid="buildings"
          initialValues={initialValues?.buildings as DropdownOptions[]}
          multiple={true}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          infiniteQueryKeys={assetsInfiniteQueryKeys}
        />
      </div>
    </form>
  );
};
export default ManageTagForm;
